// WizardWisdomPage.js — Admin dashboard for Wizard Wisdom guidelines
// Shows correction candidates from Firestore, allows publishing to community

import React, { useState, useEffect } from 'react';
import { getFirestore, collection, getDocs, addDoc, query, orderBy, limit, serverTimestamp } from 'firebase/firestore';

function WizardWisdomPage() {
  const [candidates, setCandidates] = useState([]);
  const [published, setPublished] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('candidates');

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    const db = getFirestore();

    // Tier 4 Firestore migration: read from BOTH legacy wizard_* and new harness_*
    // collections and merge (de-dup by pattern). This lets historical data stay
    // visible while new writes start populating harness_*.
    const candidateCollections = ['wizard_wisdom_candidates', 'harness_wisdom_candidates'];
    const publishedCollections = ['wizard_wisdom_published', 'harness_wisdom_published'];

    try {
      // Load candidates
      const candidateResults = await Promise.all(
        candidateCollections.map(async (name) => {
          try {
            const snap = await getDocs(query(collection(db, name), orderBy('hitCount', 'desc'), limit(100)));
            return snap.docs.map(doc => ({ id: doc.id, _source: name, ...doc.data() }));
          } catch { return []; }
        })
      );
      const candSeen = new Set();
      const mergedCandidates = [];
      for (const c of candidateResults.flat()) {
        if (!candSeen.has(c.pattern)) {
          candSeen.add(c.pattern);
          mergedCandidates.push(c);
        }
      }
      setCandidates(mergedCandidates);

      // Load published
      const publishedResults = await Promise.all(
        publishedCollections.map(async (name) => {
          try {
            const snap = await getDocs(query(collection(db, name), orderBy('publishedAt', 'desc'), limit(100)));
            return snap.docs.map(doc => ({ id: doc.id, _source: name, ...doc.data() }));
          } catch { return []; }
        })
      );
      const pubSeen = new Set();
      const mergedPublished = [];
      for (const p of publishedResults.flat()) {
        if (!pubSeen.has(p.pattern)) {
          pubSeen.add(p.pattern);
          mergedPublished.push(p);
        }
      }
      setPublished(mergedPublished);
    } catch (err) {
      console.error('Failed to load wisdom data:', err);
    }

    setLoading(false);
  }

  async function publishCandidate(candidate) {
    const db = getFirestore();
    try {
      const payload = {
        pattern: candidate.pattern,
        category: candidate.category || 'all',
        severity: candidate.hitCount >= 10 ? 'critical' : 'important',
        why: `Reported ${candidate.hitCount} times by users.`,
        hitCount: candidate.hitCount,
        publishedAt: serverTimestamp(),
        sourceDocumentID: candidate.id
      };
      // Tier 4: dual-write to legacy + new collection so both stay in sync.
      await addDoc(collection(db, 'wizard_wisdom_published'), payload);
      try { await addDoc(collection(db, 'harness_wisdom_published'), payload); } catch {}
      alert(`Published: ${candidate.pattern}`);
      loadData();
    } catch (err) {
      alert('Failed to publish: ' + err.message);
    }
  }

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '40px 20px', fontFamily: '-apple-system, sans-serif' }}>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Wizard Wisdom</h1>
      <p style={{ color: '#6b7280', marginBottom: 24 }}>
        Quality guidelines learned from user corrections. Review candidates and publish the best ones to the community.
      </p>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <button
          onClick={() => setTab('candidates')}
          style={{
            padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: tab === 'candidates' ? '#3b82f6' : '#f3f4f6',
            color: tab === 'candidates' ? '#fff' : '#374151', fontWeight: 600
          }}
        >
          Candidates ({candidates.length})
        </button>
        <button
          onClick={() => setTab('published')}
          style={{
            padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: tab === 'published' ? '#22c55e' : '#f3f4f6',
            color: tab === 'published' ? '#fff' : '#374151', fontWeight: 600
          }}
        >
          Published ({published.length})
        </button>
        <button onClick={loadData} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}>
          Refresh
        </button>
      </div>

      {loading && <p>Loading...</p>}

      {/* Candidates tab */}
      {tab === 'candidates' && !loading && (
        <div>
          {candidates.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
              <p style={{ fontSize: 18, marginBottom: 8 }}>No candidates yet</p>
              <p style={{ fontSize: 14 }}>Candidates appear when users report recurring issues in the AI Wizard.</p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                  <th style={{ padding: '8px 12px', fontSize: 12, color: '#6b7280', textTransform: 'uppercase' }}>Pattern</th>
                  <th style={{ padding: '8px 12px', fontSize: 12, color: '#6b7280' }}>Category</th>
                  <th style={{ padding: '8px 12px', fontSize: 12, color: '#6b7280' }}>Hits</th>
                  <th style={{ padding: '8px 12px', fontSize: 12, color: '#6b7280' }}>Last Seen</th>
                  <th style={{ padding: '8px 12px', fontSize: 12, color: '#6b7280' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map(c => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 12px', fontSize: 14 }}>{c.pattern}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                        background: c.category === 'card-games' ? '#fef3c7' : c.category === 'board-games' ? '#dbeafe' : '#f3f4f6',
                        color: c.category === 'card-games' ? '#92400e' : c.category === 'board-games' ? '#1e40af' : '#374151'
                      }}>
                        {c.category || 'all'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: c.hitCount >= 10 ? '#dc2626' : c.hitCount >= 5 ? '#f59e0b' : '#6b7280' }}>
                      {c.hitCount}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12, color: '#9ca3af' }}>
                      {c.lastSeen ? new Date(c.lastSeen * 1000).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <button
                        onClick={() => publishCandidate(c)}
                        style={{
                          padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                          background: '#22c55e', color: '#fff', fontSize: 12, fontWeight: 600
                        }}
                      >
                        Publish
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Published tab */}
      {tab === 'published' && !loading && (
        <div>
          {published.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
              <p style={{ fontSize: 18, marginBottom: 8 }}>No published guidelines yet</p>
              <p style={{ fontSize: 14 }}>Review candidates and publish the best ones.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {published.map(p => (
                <div key={p.id} style={{ padding: 16, background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700,
                      background: p.severity === 'critical' ? '#fef2f2' : '#fefce8',
                      color: p.severity === 'critical' ? '#dc2626' : '#ca8a04'
                    }}>
                      {(p.severity || 'important').toUpperCase()}
                    </span>
                    <span style={{ fontSize: 11, color: '#6b7280' }}>{p.category || 'all'}</span>
                  </div>
                  <p style={{ fontSize: 14, fontWeight: 600, margin: '4px 0' }}>{p.pattern}</p>
                  {p.why && <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>{p.why}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default WizardWisdomPage;
